"use client";

import { InfiniteAutocomplete } from "@xefi/x-react/form";
import { memo, useCallback, useMemo, useState, type ReactNode } from "react";

import type { Option } from "@/types/common";
import { sortByName } from "@/utils/utils";

export type SelectionKey = string | number;

type AutocompleteOption = Option & Record<string, unknown>;

type MultiSelectAutoCompleteProps = {
  name: string;
  placeholder: string;
  options: Option[];
  selected?: Option[];
  onSelect?: (value: Option[]) => void;
  className?: string;
  isFetching?: boolean;
  isLoading?: boolean;
  isInvalid?: boolean;
  errorMessage?: string;
  selectionMode?: "single" | "multiple";
  hasNextPage?: boolean;
  fetchNextPage?: () => void;
  onSearch?: (value: string) => void;
  isDisabled?: boolean;
  disabledOptionIds?: string[];
  emptyContent?: ReactNode;
};

const toAutocompleteOption = (item: Option): AutocompleteOption => ({
  ...item,
  name: item.name,
});

const MultiSelectAutoCompleteComponent = ({
  name,
  placeholder,
  options = [],
  selected = [],
  onSelect,
  className,
  selectionMode = "multiple",
  isFetching = false,
  isLoading = false,
  isInvalid = false,
  errorMessage,
  hasNextPage = false,
  fetchNextPage,
  onSearch,
  isDisabled = false,
  disabledOptionIds,
  emptyContent,
}: MultiSelectAutoCompleteProps) => {
  const [searchText, setSearchText] = useState("");

  const normalizedOptions = useMemo(
    () => sortByName(options).map(toAutocompleteOption),
    [options],
  );

  const normalizedSelected = useMemo(
    () => selected.map(toAutocompleteOption),
    [selected],
  );

  const isSingleSelection = selectionMode === "single";

  const selectedKeys = useMemo(
    () => new Set(normalizedSelected.map((item) => String(item.id))),
    [normalizedSelected],
  );

  const selectedKey = useMemo(
    () =>
      normalizedSelected.length > 0 ? String(normalizedSelected[0].id) : null,
    [normalizedSelected],
  );

  const filteredItems = useMemo(() => {
    if (!searchText) return normalizedOptions;

    const lower = searchText.toLowerCase();
    return normalizedOptions.filter((item) =>
      item.name.toLowerCase().includes(lower),
    );
  }, [normalizedOptions, searchText]);

  const inputWrapperClassName = isInvalid
    ? `
                        border w-full rounded-[6px] shadow-none bg-transparent
                        !border-danger
                        !outline-none !ring-0
                        group-data-[focus=true]:!border-danger group-data-[hover=true]:!border-danger
                    `
    : `
                        border w-full rounded-[6px] shadow-none bg-transparent
                        border-border dark:!border-[#3a3a3a]
                        !outline-none !ring-0
                        group-data-[focus=true]:!border-[#66b8ed] group-data-[hover=true]:!border-border
                        dark:group-data-[focus=true]:!border-primary-400 dark:group-data-[hover=true]:!border-[#4a4a4a]
                    `;

  const handleSelectionChange = useCallback(
    (keys: SelectionKey | Set<SelectionKey> | null) => {
      setSearchText("");

      const keySet =
        keys instanceof Set ? keys : new Set(keys != null ? [keys] : []);
      const keyArray = Array.from(keySet).map(String);
      const selectedItems = normalizedOptions.filter((item) =>
        keyArray.includes(String(item.id)),
      );

      onSelect?.(selectedItems);
    },
    [normalizedOptions, onSelect],
  );
  if (isDisabled) {
    const displayValue =
      normalizedSelected.length > 0 ? normalizedSelected[0].name : placeholder;

    return (
      <div
        aria-label={name}
        aria-disabled="true"
        title={displayValue}
        className={`flex min-h-10 items-center rounded-[6px] border border-border bg-transparent px-3 text-sm text-foreground opacity-60 cursor-not-allowed dark:!border-[#3a3a3a] dark:!text-white ${className ?? ""}`}
      >
        <span className="block w-full truncate">{displayValue}</span>
      </div>
    );
  }

  return (
    <InfiniteAutocomplete<AutocompleteOption>
      aria-label={name}
      className={className}
      color="primary"
      fetchNextPage={fetchNextPage || (() => {})}
      getItemKey={(item) => String(item.id)}
      getItemValue={(item) => item.name}
      hasNextPage={hasNextPage}
      isInvalid={isInvalid}
      errorMessage={errorMessage}
      inputProps={{
        classNames: {
          inputWrapper: inputWrapperClassName,
          input:
            "text-foreground dark:!text-white placeholder:text-gray-400 dark:placeholder:!text-white text-ellipsis overflow-hidden whitespace-nowrap placeholder:text-ellipsis placeholder:overflow-hidden",
        },
      }}
      itemClassName="h-auto min-h-0 items-start py-2"
      isFetching={isFetching}
      isLoading={isLoading}
      isDisabled={isDisabled}
      emptyContent={emptyContent}
      items={filteredItems}
      disabledKeys={disabledOptionIds}
      selectionMode={selectionMode}
      defaultInputValue={
        normalizedSelected.length > 0 ? normalizedSelected[0].name : ""
      }
      placeholder={
        normalizedSelected.length > 0 ? normalizedSelected[0].name : placeholder
      }
      popoverBackdrop="transparent"
      popoverProps={{
        children: [],
        classNames: {
          content: "min-w-[420px] w-[560px] max-w-[calc(100vw-1rem)]",
        },
      }}
      radius="lg"
      renderItem={(item) => (
        <span className="block max-w-full whitespace-normal break-words text-left leading-5">
          {item.name}
        </span>
      )}
      selectedKey={isSingleSelection ? selectedKey : undefined}
      selectedKeys={selectedKeys}
      selectionBadgeClassName="hidden"
      variant="bordered"
      onSearchChange={(value) => {
        setSearchText(value || "");
        onSearch?.(value || "");
      }}
      onSelectionChange={handleSelectionChange}
    />
  );
};

export const MultiSelectAutoComplete = memo(MultiSelectAutoCompleteComponent);
